import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getExperts } from "../api";
import HamburgerMenu from "../components/HamburgerMenu";

interface Expert {
  proId: string;
  name: string;
  businessName?: string;
  serviceCategory: string;
  hourlyRate: number;
  rating: number;
  bio: string;
  verified: boolean;
  photoUrl?: string;
  city?: string;
  experience?: string;
  hireCount?: number;
  acceptanceRate?: number;
  isOnline?: boolean;
  reviewCount?: number;
  responseTime?: string;
}

const POPULAR_SERVICES = [
  { id: "Cleaning", label: "House Cleaning", icon: "🧹", color: "from-blue-500 to-blue-600" },
  { id: "Plumbing", label: "Plumbing", icon: "🔧", color: "from-cyan-500 to-blue-500" },
  { id: "Electrical", label: "Electrical", icon: "⚡", color: "from-yellow-500 to-orange-500" },
  { id: "Painting", label: "Painting", icon: "🎨", color: "from-purple-500 to-pink-500" },
  { id: "Outdoor", label: "Lawn & Yard", icon: "🌿", color: "from-green-500 to-emerald-600" },
  { id: "Moving", label: "Moving", icon: "🚚", color: "from-orange-500 to-red-500" },
  { id: "HVAC", label: "Heating & AC", icon: "❄️", color: "from-sky-500 to-cyan-600" },
  { id: "Roofing", label: "Roofing", icon: "🏠", color: "from-stone-500 to-stone-700" },
  { id: "Flooring", label: "Flooring", icon: "🪵", color: "from-amber-600 to-yellow-700" },
  { id: "Carpentry", label: "Carpentry", icon: "🔨", color: "from-amber-700 to-orange-800" },
  { id: "Pools", label: "Pool Service", icon: "🏊", color: "from-cyan-400 to-blue-500" },
  { id: "Concrete", label: "Concrete", icon: "🧱", color: "from-gray-500 to-gray-700" },
];

export default function ExplorePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(searchParams.get("category") || "all");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");

  useEffect(() => {
    setLoading(true);
    getExperts().then(data => setExperts(data || []))
      .catch(() => setExperts([]))
      .finally(() => setLoading(false));
  }, []);

  let filtered = experts;
  if (activeCategory !== "all") {
    filtered = filtered.filter(e => e.serviceCategory?.toLowerCase().includes(activeCategory.toLowerCase()));
  }
  if (searchQuery && searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(e =>
      (e.businessName || "").toLowerCase().includes(q) ||
      (e.name || "").toLowerCase().includes(q) ||
      (e.serviceCategory || "").toLowerCase().includes(q) ||
      (e.bio || "").toLowerCase().includes(q) ||
      (e.city || "").toLowerCase().includes(q)
    );
  }

  const topRated = [...filtered].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 6);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center gap-3">
          <HamburgerMenu role="client" />
          <div className="flex items-center gap-2">
            <svg width="32" height="32" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="eLg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4C6EF5"/><stop offset="100%" stopColor="#1A3CE8"/></linearGradient>
                <linearGradient id="eLg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#20C997"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
                <linearGradient id="eBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
              </defs>
              <rect width="52" height="52" rx="14" fill="url(#eBg)"/>
              <g transform="translate(4,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.95"/><circle cx="11" cy="10" r="5" fill="url(#eLg1)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
              <g transform="translate(25,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.85"/><circle cx="11" cy="10" r="5" fill="url(#eLg2)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
            </svg>
            <span className="font-display text-lg font-bold text-navy">Mouna</span>
          </div>
        </div>
      </header>

      {/* Hero with search */}
      <div className="bg-gradient-to-br from-mouna-blue via-blue-600 to-navy px-4 pt-8 pb-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-white font-display text-2xl sm:text-3xl font-bold mb-2 leading-tight">
            What service do you need?
          </h1>
          <p className="text-blue-100 text-sm mb-5">Get matched with top-rated local pros in minutes</p>

          <form onSubmit={handleSearch} className="bg-white rounded-2xl p-1.5 shadow-2xl">
            <div className="flex flex-col sm:flex-row gap-1">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">🔍</span>
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Service or business name"
                  className="w-full pl-10 pr-4 py-3 text-sm outline-none rounded-xl text-navy"/>
              </div>
              <button type="submit" className="bg-mouna-blue hover:bg-blue-700 text-white rounded-xl px-6 py-3 text-sm font-bold transition-colors">
                Search
              </button>
            </div>
          </form>

          <div className="flex items-center gap-4 mt-4 text-xs text-blue-100">
            <span>✓ Houston-local pros</span>
            <span>✓ Client reviews</span>
            <span>✓ Free quotes</span>
          </div>
        </div>
      </div>

      {/* Popular services grid */}
      <div className="px-4 py-8 max-w-6xl mx-auto w-full">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-xl font-bold text-navy">Popular services</h2>
          <button onClick={() => setActiveCategory("all")} className="text-xs text-mouna-blue font-bold">View all →</button>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {POPULAR_SERVICES.map(svc => (
            <button key={svc.id} onClick={() => setActiveCategory(svc.id)}
              className={"group flex flex-col items-center gap-2 p-3 rounded-2xl bg-white hover:shadow-lg transition-all border-2 " +
                (activeCategory === svc.id ? "border-mouna-blue ring-2 ring-blue-100" : "border-transparent")}>
              <div className={"w-14 h-14 rounded-2xl bg-gradient-to-br " + svc.color + " flex items-center justify-center text-2xl shadow-md group-hover:scale-110 transition-transform"}>
                {svc.icon}
              </div>
              <span className="text-xs font-bold text-navy text-center leading-tight">{svc.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Top Rated Pros - featured row */}
      {topRated.length > 0 && activeCategory === "all" && !searchQuery && (
        <div className="px-4 pb-8 max-w-6xl mx-auto w-full">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h2 className="font-display text-xl font-bold text-navy">⭐ Top-rated near you</h2>
              <p className="text-xs text-gray-500 mt-0.5">Verified pros customers love</p>
            </div>
          </div>

          <div className="flex gap-3 overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide">
            {topRated.map(expert => (
              <button key={expert.proId} onClick={() => navigate(`/pro/${expert.proId}`)}
                className="flex-shrink-0 w-64 bg-white rounded-2xl shadow-card hover:shadow-lg transition-shadow text-left overflow-hidden">
                <div className="aspect-square bg-gradient-to-br from-blue-pale to-gray-100 relative">
                  {expert.photoUrl ? (
                    <img src={expert.photoUrl} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl opacity-40">👷</div>
                  )}
                  {expert.verified && (
                    <div className="absolute top-2 left-2 bg-white text-teal text-xs font-bold rounded-full px-2 py-1">✓ Verified</div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-navy truncate text-sm">{expert.businessName || expert.name}</h3>
                  <p className="text-xs text-gray-500 truncate mb-1">{expert.serviceCategory}</p>
                  <div className="flex items-center gap-2 text-xs">
                    <span>⭐ {expert.rating?.toFixed(1) || "New"}</span>
                    <span className="text-gray-300">•</span>
                    <span className="font-bold text-navy">${expert.hourlyRate}/hr</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Browse all pros */}
      <div className="px-4 pb-8 max-w-6xl mx-auto w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold text-navy">
            {activeCategory === "all" ? "All Pros" : `${activeCategory} Pros`}
            <span className="text-sm font-normal text-gray-400 ml-2">({filtered.length})</span>
          </h2>
          {activeCategory !== "all" && (
            <button onClick={() => setActiveCategory("all")} className="text-xs text-mouna-blue font-bold">Clear filter</button>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-4 animate-pulse flex gap-4">
                <div className="w-24 h-24 bg-gray-100 rounded-xl flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-100 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="text-5xl mb-3">🔍</div>
            <h3 className="font-bold text-navy mb-2">No pros found</h3>
            <p className="text-sm text-gray-500 mb-4">Try a different category or search term</p>
            <button onClick={() => { setActiveCategory("all"); setSearchQuery(""); }}
              className="bg-mouna-blue text-white rounded-full px-6 py-2.5 text-sm font-bold">
              Browse all pros
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(expert => (
              <button key={expert.proId} onClick={() => navigate(`/pro/${expert.proId}`)}
                className="w-full bg-white rounded-2xl p-4 hover:shadow-lg transition-shadow text-left flex gap-4">

                {/* Photo */}
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-pale to-gray-100 relative">
                  {expert.photoUrl ? (
                    <img src={expert.photoUrl} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl opacity-40">👷</div>
                  )}
                  {expert.isOnline && (
                    <div className="absolute bottom-1 right-1 w-3 h-3 bg-teal border-2 border-white rounded-full"></div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <h3 className="font-bold text-navy text-sm sm:text-base truncate">{expert.businessName || expert.name}</h3>
                        {expert.verified && <span className="bg-teal-light text-teal text-xs font-bold rounded-full px-1.5 py-0.5">✓</span>}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500">{expert.serviceCategory}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-base sm:text-lg font-bold text-navy">${expert.hourlyRate}</div>
                      <div className="text-xs text-gray-400">/hr</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                    <span className="flex items-center gap-1">⭐ <span className="font-bold text-navy">{expert.rating?.toFixed(1) || "New"}</span> {expert.reviewCount ? `(${expert.reviewCount})` : ""}</span>
                    {(expert.hireCount || 0) > 0 && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span>{expert.hireCount} hires</span>
                      </>
                    )}
                    {expert.responseTime && (
                      <>
                        <span className="text-gray-300">•</span>
                        <span>⏱ {expert.responseTime}</span>
                      </>
                    )}
                  </div>

                  {expert.bio && (
                    <p className="text-xs text-gray-600 line-clamp-2 mb-2">{expert.bio}</p>
                  )}

                  <div className="flex items-center gap-2">
                    {expert.isOnline && <span className="bg-teal-light text-teal text-xs font-bold rounded-full px-2 py-1">🟢 Online</span>}
                    {expert.city && <span className="text-xs text-gray-400">📍 {expert.city}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-2 z-30">
        <div className="flex items-center justify-around max-w-md mx-auto">
          {[
            { icon: "🏠", label: "Home", path: "/home" },
            { icon: "🔍", label: "Explore", path: "/explore", active: true },
            { icon: "📅", label: "Bookings", path: "/bookings" },
            { icon: "👤", label: "Profile", path: "/profile" },
          ].map(nav => (
            <button key={nav.label} onClick={() => navigate(nav.path)}
              className={"flex flex-col items-center gap-0.5 px-3 py-2 " + (nav.active ? "text-mouna-blue" : "text-gray-400")}>
              <span className="text-xl">{nav.icon}</span>
              <span className="text-xs font-bold">{nav.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
